
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sendSwapUpdateNotification } from "../notifications";
import { verifyCreditBalance } from "@/services/swapRequests/credit/creditOfferHandler";

// Edit a swap request
export const editSwapRequest = async (
  swapRequestId: string,
  creditOffer: number,
  message: string
): Promise<boolean> => {
  try {
    // Get current user
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      toast.error("You must be logged in to update a swap request");
      return false;
    }

    const userId = sessionData.session.user.id;
    console.log("Editing swap request:", { swapRequestId, creditOffer, userId });
    
    // Get swap request details to ensure it belongs to this user
    const { data: swapRequest, error: fetchError } = await supabase
      .from("swap_requests")
      .select(`
        id, 
        reagent_id, 
        requester_id,
        reagents!reagent_id(name, user_id)
      `)
      .eq("id", swapRequestId)
      .single();
    
    if (fetchError || !swapRequest) {
      console.error("Error fetching swap request:", fetchError);
      toast.error("Failed to verify swap request");
      return false;
    }
    
    // Verify ownership
    if (swapRequest.requester_id !== userId) {
      console.error("Ownership verification failed:", {
        requesterId: swapRequest.requester_id,
        currentUserId: userId
      });
      toast.error("You don't have permission to update this swap request");
      return false;
    }
    
    // Verify user has sufficient credits if credit offer is being made
    if (creditOffer > 0) {
      const hasEnoughCredits = await verifyCreditBalance(userId, creditOffer);
      if (!hasEnoughCredits) {
        toast.error(`You don't have enough credits for this offer. Required: ${creditOffer}`);
        return false;
      }
      console.log(`Credit verification passed for user ${userId}, credit offer: ${creditOffer}`);
    }
    
    // Update swap request
    const { error: updateError } = await supabase
      .from("swap_requests")
      .update({ 
        credit_offer: creditOffer,
        updated_at: new Date().toISOString() 
      })
      .eq("id", swapRequestId);
      
    if (updateError) {
      console.error("Error updating swap request:", updateError);
      toast.error("Failed to update swap request");
      return false;
    }
    
    // Send a notification to the reagent owner
    const reagentOwnerId = swapRequest.reagents?.user_id;
    if (reagentOwnerId) {
      const actionButton = {
        actionButton: {
          text: "View Swap Request",
          url: `/swap-request/${swapRequestId}`,
          type: "secondary"
        }
      };

      const { error: messageError } = await supabase
        .from("messages")
        .insert({
          sender_id: userId,
          recipient_id: reagentOwnerId,
          reagent_id: swapRequest.reagent_id,
          content: `I've updated my swap request for ${swapRequest.reagents.name}. New credit offer: ${creditOffer}. ${message}`,
          read: false,
          payload: actionButton
        });
        
      if (messageError) {
        console.error("Error sending notification message:", messageError);
        toast.warning("Swap request updated, but failed to send notification");
      }
      
      // Send email notification to the reagent owner
      sendSwapUpdateNotification(
        swapRequestId,
        swapRequest.reagents.name,
        reagentOwnerId,
        "updated",
        message
      ).catch(error => {
        console.error("Failed to send update notification email:", error);
      });
    }
    
    console.log("Swap request updated successfully:", { swapRequestId, newCreditOffer: creditOffer });
    toast.success("Swap request updated successfully");
    return true;
  } catch (error) {
    console.error("Exception in editSwapRequest:", error);
    toast.error("An unexpected error occurred");
    return false;
  }
};
